// Browser-side identity for Dash0 telemetry. All functions are safe no-ops
// when the SDK is uninitialized (local dev) and during SSR renders of client
// components — the SDK only mutates module state until signals are sent.

import { addSignalAttribute, identify, removeSignalAttribute } from '@dash0/sdk-web';

import { getCachedProfileIdentity } from '@src/utils/profile-cache-cookie';

import { IDENTITY_ATTRIBUTE_KEYS, IdentityIds, toIdentityAttributes } from './identity-attributes';

type Dash0User = {
  profileId: string;
  guestId?: string;
};

// The profile id goes through identify() rather than addSignalAttribute so
// Dash0's Sessions UI attributes the session to a user. Deliberately no
// email/name — `user.id` only. The PMS guest id always travels with it as
// numa.guest.id. Returns the guest-attribute cleanup (see below).
export function identifyDash0User({ profileId, guestId }: Dash0User): () => void {
  identify(profileId);
  return addDash0IdentityAttributes({ guestId });
}

// identify() without arguments removes every user.* signal attribute.
export function clearDash0User() {
  identify();
  removeSignalAttribute(IDENTITY_ATTRIBUTE_KEYS.guestId);
}

/**
 * One-shot pre-hydration seed, called by initDash0() right before init()
 * (init() records the initial page-view synchronously, so the seed must
 * already be in place): attributes the session to the profile cached in the
 * cookie (validated against the current Cognito token) so signals emitted
 * before the React owners mount already carry user.id / numa.guest.id. Deliberately not an
 * owner: it never cleans up — the owners re-identify with the same or
 * fresher values, and logout clears via clearDash0User().
 */
export function seedDash0IdentityFromProfileCache() {
  const cachedIdentity = getCachedProfileIdentity();
  if (cachedIdentity) {
    identifyDash0User(cachedIdentity);
  }
}

/**
 * Stamps the given identifiers on every subsequent browser signal (web
 * events, errors, instrumented requests). Returns a cleanup that removes
 * exactly the keys it set — pass it as a React effect destructor so
 * attributes don't leak onto signals from unrelated routes.
 */
export function addDash0IdentityAttributes(ids: Omit<IdentityIds, 'profileId'>): () => void {
  const attributes = toIdentityAttributes(ids);
  for (const [key, value] of Object.entries(attributes)) {
    addSignalAttribute(key, value);
  }
  return () => {
    for (const key of Object.keys(attributes)) {
      removeSignalAttribute(key);
    }
  };
}
